<template>
  <div class="recurrence-calendar">
    <div class="calendar-header-title">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
        <line x1="16" x2="16" y1="2" y2="6"></line>
        <line x1="8" x2="8" y1="2" y2="6"></line>
        <line x1="3" x2="21" y1="10" y2="10"></line>
      </svg>
      <span>Datas previstas de disparo</span>
      <div v-if="parsedEnd" class="calendar-nav">
        <q-btn flat dense round icon="chevron_left" size="xs" :disable="!canGoPrev" @click="prevMonths" />
        <q-btn flat dense round icon="chevron_right" size="xs" :disable="!canGoNext" @click="nextMonths" />
      </div>
    </div>
    <div class="calendars-row">
      <div v-for="(month, idx) in currentMonths" :key="idx" class="month-block">
        <div class="month-title">{{ month.label }}</div>
        <div class="weekdays">
          <span v-for="d in weekdays" :key="d">{{ d }}</span>
        </div>
        <div class="days-grid">
          <span
            v-for="(cell, ci) in month.cells"
            :key="ci"
            :class="['day-cell', {
              'day-empty': !cell.day,
              'day-start': cell.isStart,
              'day-last': cell.isLast,
              'day-end': cell.isEnd,
              'day-dispatch': cell.isDispatch,
              'day-today': cell.isToday
            }]"
          >
            {{ cell.day || '' }}
          </span>
        </div>
        <div v-if="month.dispatches.length" class="dispatch-count">
          {{ month.dispatches.length }} disparo{{ month.dispatches.length > 1 ? 's' : '' }} neste mês
        </div>
      </div>
    </div>
    <div v-if="totalDispatches > 0" class="dispatch-legend">
      <span class="legend-badge start">Início</span>
      <span class="legend-badge dispatch">Disparo</span>
      <span class="legend-badge last">Último disparo</span>
      <span class="legend-badge end">Data fim</span>
    </div>
  </div>
</template>

<script>
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { repository } from 'jtech'

const CAMPAIGN_CALCULATE_EXECUTION_DATES_INTERFACE = 'campaign.calculateExecutionDates'

@Component({ name: 'RecurrenceCalendar' })
class RecurrenceCalendar extends Vue {
  @Prop({ type: [String, Number], default: null }) campaignId
  @Prop({ type: String, default: '' }) startDate
  @Prop({ type: [String, Number], default: null }) recurrenceType
  @Prop({ type: Number, default: null }) customInterval
  @Prop({ type: [String, Number], default: null }) leadTimeDays
  @Prop({ type: [String, Number], default: null }) messagingPurpose
  @Prop({ type: String, default: '' }) endDate

  weekdays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
  monthOffset = 0
  executionDates = []
  requestToken = 0

  get parsedStart () {
    return this.parseStrictInputDate(this.startDate)
  }

  get parsedEnd () {
    return this.parseStrictInputDate(this.endDate)
  }

  get intervalDays () {
    if (!this.recurrenceType) return null
    const key = Number(this.recurrenceType)
    if (key === 50510003) return 7
    if (key === 50510004) return 14
    if (key === 50510005) return 30
    if (key === 50510006) return this.customInterval || null
    return null
  }

  get isMonthly () {
    return Number(this.recurrenceType) === 50510005
  }

  get canGoPrev () {
    if (!this.parsedStart) return false
    const base = this.baseDate
    const visibleMonth = new Date(base.getFullYear(), base.getMonth() + this.monthOffset, 1)
    const startMonth = new Date(this.parsedStart.getFullYear(), this.parsedStart.getMonth(), 1)
    return visibleMonth.getTime() > startMonth.getTime()
  }

  get canGoNext () {
    if (!this.parsedEnd) return false
    const base = this.baseDate
    const totalMonths = base.getMonth() + this.monthOffset + 1
    const year = base.getFullYear() + Math.floor(totalMonths / 12)
    const month = ((totalMonths % 12) + 12) % 12
    const endYear = this.parsedEnd.getFullYear()
    const endMonth = this.parsedEnd.getMonth()
    return year < endYear || (year === endYear && month < endMonth)
  }

  prevMonths () {
    if (this.canGoPrev) this.monthOffset -= 1
  }

  nextMonths () {
    if (this.canGoNext) this.monthOffset += 1
  }

  @Watch('startDate')
  onStartDateChange () {
    this.monthOffset = 0
  }

  @Watch('campaignId', { immediate: true })
  async onCampaignIdChange () {
    await this.fetchExecutionDates()
  }

  @Watch('startDate', { immediate: true })
  async onStartDateOrParamsChange () {
    await this.fetchExecutionDates()
  }

  @Watch('endDate', { immediate: true })
  async onEndDateChange () {
    await this.fetchExecutionDates()
  }

  @Watch('recurrenceType', { immediate: true })
  async onRecurrenceTypeChange () {
    await this.fetchExecutionDates()
  }

  @Watch('customInterval', { immediate: true })
  async onCustomIntervalChange () {
    await this.fetchExecutionDates()
  }

  @Watch('leadTimeDays', { immediate: true })
  async onLeadTimeDaysChange () {
    await this.fetchExecutionDates()
  }

  @Watch('messagingPurpose', { immediate: true })
  async onMessagingPurposeChange () {
    await this.fetchExecutionDates()
  }

  get baseDate () {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), 1)
  }

  get hasRequiredParams () {
    const hasStart = this.isValidBackendDate(this.startDate)
    const hasRecurrenceType = this.recurrenceType !== null && this.recurrenceType !== undefined && this.recurrenceType !== ''

    if (!hasStart || !hasRecurrenceType) {
      return false
    }

    // Data fim obrigatória
    if (!this.endDate) {
      return false
    }

    // Enquanto o usuário digita, não dispara cálculo com data parcial/inválida.
    if (!this.isDateInputReady(this.endDate)) {
      return false
    }

    if (this.endDate) {
      const start = this.parseDateValue(this.startDate)
      const end = this.parseDateValue(this.endDate)
      if (!start || !end || end.getTime() < start.getTime()) {
        return false
      }
    }

    return true
  }

  isDateInputReady (value) {
    if (value === null || value === undefined || value === '') {
      return true
    }
    return this.isValidBackendDate(value)
  }

  isValidBackendDate (value) {
    return !!this.parseStrictInputDate(value)
  }

  parseStrictInputDate (value) {
    if (typeof value !== 'string') return null

    const normalized = value.trim()
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) return null

    const [d, m, y] = normalized.split('/').map(Number)
    if (!d || !m || !y || y < 1753) return null

    const date = new Date(y, m - 1, d)
    if (isNaN(date.getTime())) return null
    if (date.getFullYear() !== y || date.getMonth() !== (m - 1) || date.getDate() !== d) return null

    return date
  }

  parseDateValue (value) {
    if (!value) return null

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate())
    }

    if (typeof value === 'number') {
      const date = new Date(value)
      return isNaN(date.getTime()) ? null : new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }

    if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value.trim())) {
      const strict = this.parseStrictInputDate(value)
      return strict ? new Date(strict.getFullYear(), strict.getMonth(), strict.getDate()) : null
    }

    if (typeof value === 'string') {
      const date = new Date(value)
      return isNaN(date.getTime()) ? null : new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }

    return null
  }

  extractExecutionDateValues (response) {
    if (!response) return []

    if (Array.isArray(response)) return response

    const arrayCandidates = [
      response.executionDates,
      response.dates,
      response.registros,
      response.data,
      response.items
    ]

    const firstArray = arrayCandidates.find(item => Array.isArray(item))
    return firstArray || []
  }

  normalizeExecutionDates (response) {
    const values = this.extractExecutionDateValues(response)
    const unique = new Map()

    values.forEach(item => {
      const raw = (item && typeof item === 'object')
        ? (item.executionDate || item.date || item.dtExecucao || item.startDate || item)
        : item
      const parsed = this.parseDateValue(raw)
      if (!parsed) return

      const key = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
      unique.set(key, parsed)
    })

    return Array.from(unique.values()).sort((a, b) => a.getTime() - b.getTime())
  }

  async fetchExecutionDates () {
    if (!this.hasRequiredParams) {
      this.executionDates = []
      return
    }

    const token = ++this.requestToken

    try {
      const payload = {
        id: this.campaignId || null,
        startDate: this.startDate || null,
        endDate: this.endDate || null,
        recurrenceType: this.recurrenceType != null ? Number(this.recurrenceType) : null,
        customInterval: this.customInterval != null ? Number(this.customInterval) : null,
        leadTimeDays: this.leadTimeDays != null ? Number(this.leadTimeDays) : null,
        messagingPurpose: this.messagingPurpose != null ? Number(this.messagingPurpose) : null
      }

      const response = await repository.resolveLogic(CAMPAIGN_CALCULATE_EXECUTION_DATES_INTERFACE, payload)

      if (token !== this.requestToken) return
      this.executionDates = this.normalizeExecutionDates(response)
    } catch (error) {
      if (token !== this.requestToken) return
      this.executionDates = []
      console.warn('Erro ao calcular datas de disparo no backend:', error)
    }
  }

  get currentMonths () {
    const base = this.baseDate
    const result = []
    for (let i = 0; i < 2; i++) {
      const totalMonths = base.getMonth() + this.monthOffset + i
      const year = base.getFullYear() + Math.floor(totalMonths / 12)
      const month = ((totalMonths % 12) + 12) % 12
      result.push(this.buildMonth(year, month))
    }
    return result
  }

  get totalDispatches () {
    return this.currentMonths.reduce((acc, m) => acc + m.dispatches.length, 0)
  }

  nextOccurrence (current) {
    const originalDay = this.parsedStart.getDate()
    let nextMonth = current.getMonth() + 1
    let nextYear = current.getFullYear()
    if (nextMonth > 11) {
      nextMonth = 0
      nextYear += 1
    }
    let targetDay = originalDay
    while (targetDay > 0) {
      const attempt = new Date(nextYear, nextMonth, targetDay)
      if (attempt.getMonth() === nextMonth) return attempt
      targetDay -= 1
    }
  }

  advanceCursor (cursor) {
    if (this.isMonthly) {
      return this.nextOccurrence(cursor)
    }
    return new Date(cursor.getTime() + this.intervalDays * 86400000)
  }

  dispatchDaysInMonth (year, month) {
    return this.executionDates
      .filter(date => date.getFullYear() === year && date.getMonth() === month)
      .map(date => date.getDate())
  }

  get lastDispatchDate () {
    return this.executionDates.length > 0
      ? this.executionDates[this.executionDates.length - 1]
      : null
  }

  buildMonth (year, month) {
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

    const start = this.parsedStart
    const end = this.parsedEnd
    const dispatches = this.dispatchDaysInMonth(year, month)
    const dispatchSet = new Set(dispatches)

    const lastDate = this.lastDispatchDate
    let lastDispatchDay = null
    if (lastDate &&
        lastDate.getFullYear() === year &&
        lastDate.getMonth() === month) {
      lastDispatchDay = lastDate.getDate()
    }

    const firstWeekday = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const today = new Date()

    const cells = []
    for (let i = 0; i < firstWeekday; i++) cells.push({ day: null })

    for (let d = 1; d <= daysInMonth; d++) {
      const isStart = !!(start &&
        start.getFullYear() === year &&
        start.getMonth() === month &&
        start.getDate() === d)
      const isToday = (today.getFullYear() === year &&
        today.getMonth() === month &&
        today.getDate() === d)
      const isLast = !isStart && (lastDispatchDay === d)
      const isEnd = !!(end &&
        end.getFullYear() === year &&
        end.getMonth() === month &&
        end.getDate() === d)

      cells.push({
        day: d,
        isStart,
        isDispatch: !isStart && !isLast && dispatchSet.has(d),
        isLast,
        isEnd: !isStart && !isLast && isEnd,
        isToday
      })
    }

    return {
      label: `${monthNames[month]} ${year}`,
      cells,
      dispatches
    }
  }
}
export default RecurrenceCalendar
</script>

<style lang="stylus" scoped>
.recurrence-calendar
  background: #f8fafc
  border: 1px solid #e8ecf0
  border-radius: 10px
  padding: 14px 16px 10px
  margin-top: 12px

.calendar-header-title
  display: flex
  align-items: center
  gap: 6px
  font-size: 12px
  font-weight: 600
  color: #52525b
  margin-bottom: 12px

.calendar-nav
  margin-left: auto
  display: flex
  gap: 2px

.calendars-row
  display: flex
  gap: 16px
  flex-wrap: wrap

.month-block
  flex: 1
  min-width: 150px

.month-title
  font-size: 12px
  font-weight: 600
  color: #0e88e4
  text-align: center
  margin-bottom: 6px

.weekdays
  display: grid
  grid-template-columns: repeat(7, 1fr)
  text-align: center
  span
    font-size: 10px
    font-weight: 600
    color: #a1a1aa
    padding: 2px 0

.days-grid
  display: grid
  grid-template-columns: repeat(7, 1fr)
  gap: 2px
  margin-top: 4px

.day-cell
  height: 24px
  width: 24px
  display: flex
  align-items: center
  justify-content: center
  border-radius: 50%
  font-size: 11px
  color: #3f3f46
  cursor: default
  justify-self: center

.day-empty
  visibility: hidden

.day-today
  font-weight: 700
  color: #0e88e4

.day-start
  background: #0e88e4
  color: #fff
  font-weight: 700

.day-dispatch
  background: #dcfce7
  color: #16a34a
  font-weight: 600

.day-last
  background: #ffedd5
  color: #ea580c
  font-weight: 700
  border: 1px solid #fdba74

.day-end
  background: #fee2e2
  color: #dc2626
  font-weight: 700
  border: 1px solid #fca5a5

.dispatch-count
  font-size: 10px
  color: #71717a
  text-align: center
  margin-top: 6px

.dispatch-legend
  display: flex
  align-items: center
  justify-content: center
  flex-wrap: wrap
  gap: 6px
  margin-top: 10px

.legend-badge
  font-size: 10px
  font-weight: 600
  padding: 2px 8px
  border-radius: 99px
  &.start
    background: #0e88e4
    color: #fff
  &.dispatch
    background: #dcfce7
    color: #16a34a
    border: 1px solid #86efac
  &.last
    background: #ffedd5
    color: #ea580c
    border: 1px solid #fdba74
  &.end
    background: #fee2e2
    color: #dc2626
    border: 1px solid #fca5a5
</style>
